<script setup>
  import useVuelidate from '@vuelidate/core'

  import { computed } from 'vue'

  import FieldRendererLabel from '@/features/settingsManagement/fieldManagement/edit/plugin/FieldRendererLabel.vue'
  import FieldRendererValidationMessages from '@/features/settingsManagement/fieldManagement/edit/plugin/FieldRendererValidationMessages.vue'
  import FieldRendererWrapper from '@/features/settingsManagement/fieldManagement/edit/plugin/FieldRendererWrapper.vue'
  import { useFieldPlugins } from '@/features/settingsManagement/fieldManagement/edit/plugin/fieldsPlugin.js'
  import {
    fieldCommonProps,
    normalizeFieldExtraData,
  } from '@/features/settingsManagement/fieldManagement/edit/utils/fieldUtils.js'
  import FormRadio from '@/features/ui/components/forms/FormRadio.vue'
  import { mergeDeep } from '@/features/utils/object.utils.js'

  const emit = defineEmits(['update:modelValue'])

  const props = defineProps({
    ...fieldCommonProps,
    modelValue: {
      type: [String, Number, null],
      default: null,
    },
  })

  const { getPlugin } = useFieldPlugins()
  const plugin = getPlugin('radio')

  const extraData = computed(() => {
    return mergeDeep(plugin.defaultExtraData, normalizeFieldExtraData(props.field.extraData))
  })

  const rules = computed(() => ({
    value: plugin.getValidations({ props }),
  }))

  const state = computed(() => ({
    value: props.modelValue,
  }))

  const v$ = useVuelidate(rules, state)

  async function validate() {
    const result = await v$.value.$validate()
    return {
      isValid: result,
      errors: Object.entries(v$.value.$errors).map(([, error]) => error.$message),
    }
  }

  const options = computed(() => extraData.value.options || [])

  defineExpose({
    validate,
  })

  const innerValue = computed({
    get: () => props.modelValue,
    set: newValue => emit('update:modelValue', newValue),
  })
</script>

<template>
  <FieldRendererWrapper :field="field">
    <template #label>
      <FieldRendererLabel :field="field" :external-required="externalRequired" />
    </template>

    <div class="field-radiobuttons">
      <div v-for="option of options" :key="option.value" class="field-radiobutton">
        <FormRadio
          v-model="innerValue"
          type="radio"
          :name="field.key"
          :value="option.value"
          :checked="modelValue === option.value"
          :inputId="option.value"
          :label="option.label"
          :disabled="disabled"
          :invalid="v$.value.$error || hasExternalError"
        />
      </div>
    </div>

    <template #validation>
      <FieldRendererValidationMessages v-if="v$.value.$error" :error-messages="v$.value.$errors" />
    </template>
  </FieldRendererWrapper>
</template>

<style scoped>
  .field-radiobuttons {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
</style>
